import { FC, useState, useEffect, useMemo } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { sample } from 'lodash'
import { lazy, Suspense } from 'react'

import Styles from './Avatar.module.scss'
import { ReactComponent as LogoIcon } from 'assets/icons/blazeWhiteBg.svg'
import { optimizedImageUrlString } from 'utils'
import { useBlazeAvatar } from 'hooks/useBlazeAvatar'
import { ReactComponent as DocEmoji } from 'assets/icons/document24.svg'
import { RenderedSVGComponent } from 'types'

const letterIcons = import.meta.glob('./svgs/*.tsx') as Record<
  string,
  () => Promise<{ default: React.ComponentType<{ size?: number; color?: string }> }>
>

const MAX_IMAGE_SIZE = 500
const DEFAULT_AVATAR_COLORS = [
  '#616875',
  '#A3AAB8',
  '#0F4170',
  '#287FBD',
  '#9ED5F2',
  '#006361',
  '#00A39E',
  '#89DCDB',
  '#845210',
  '#EDB62C',
  '#DD4740',
  '#ED847E',
  '#4D2873',
  '#8754BA',
  '#8C2360',
  '#E55CAC',
  '#853B07',
  '#ED7C2C',
]

export interface Props {
  altText?: string
  className?: string
  backgroundColor?: string
  color?: string
  emoji?: string | RenderedSVGComponent
  label?: string
  loading?: boolean
  size?: number
  url?: string | null
  seed?: number
  title?: string
  randomColor?: boolean
  staticColor?: boolean
  onClick?: (e: React.MouseEvent) => void
  workspaceId?: number
  borderColor?: string
}

const emojiMultiplier = 0.5
const fontSizeMultiplier = [0.625, 0.5, 0.35, 0.275, 0.225]

const BlazeAvatar: FC<React.PropsWithChildren<Props>> = ({
  label,
  seed,
  url,
  altText,
  className,
  size = 42,
  backgroundColor,
  color,
  randomColor,
  staticColor,
  emoji,
  loading = false,
  title,
  onClick,
  workspaceId,
  borderColor,
}) => {
  const [error, setError] = React.useState(false)
  const optimizedSize = Math.min(size * 2, MAX_IMAGE_SIZE)
  const [avatarColor, setAvatarColor] = useState<string | null>(null)
  const { blazeAvatarColor, letterFile } = useBlazeAvatar(label?.slice(0, 1) || '', workspaceId)
  const importLetterIcon = letterFile ? letterIcons[`./svgs/${letterFile}`] : undefined
  const LetterIcon = useMemo(
    () =>
      importLetterIcon
        ? (lazy(importLetterIcon) as React.ComponentType<React.PropsWithChildren<{ size?: number }>>)
        : null,
    [importLetterIcon],
  )
  const modifiedSize = Math.floor(size * fontSizeMultiplier[label?.length ? label.length - 1 : 0])
  const [content, setContent] = useState(<div className={Styles.loading} />)
  const [modifiedLabel, setModifiedLabel] = useState<string | undefined>(undefined)

  useEffect(() => {
    const labelString = label ?? (typeof emoji === 'string' ? emoji : undefined)
    setModifiedLabel(labelString ? labelString.slice(0, 1) : '')
  }, [label, emoji])

  useEffect(() => {
    if (loading) {
      setContent(<div className={Styles.loading} />)
    } else if (url && !error) {
      setContent(
        <img
          onError={() => {
            setError(true)
          }}
          src={optimizedImageUrlString(url, optimizedSize.toString())}
          alt={`${altText || modifiedLabel} profile`}
          draggable="false"
        />,
      )
    } else if (blazeAvatarColor && LetterIcon) {
      setContent(
        <Suspense fallback={<div />}>
          <LetterIcon size={modifiedSize} />
        </Suspense>,
      )
    } else if (modifiedLabel) {
      setContent(<span>{modifiedLabel}</span>)
    } else if (emoji && typeof emoji !== 'string') {
      setContent(emoji || <DocEmoji className={Styles.docEmoji} />)
    } else if (seed) {
      setContent(<LogoIcon style={{ height: size, width: size }} className={Styles.logo} />)
    }
  }, [loading, url, blazeAvatarColor, modifiedLabel, seed, emoji, LetterIcon]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (blazeAvatarColor) {
      setAvatarColor(blazeAvatarColor)
      return
    }
    if (randomColor) {
      const color = sample(DEFAULT_AVATAR_COLORS) as string
      setAvatarColor(color)
      return
    }

    if (staticColor && !!label) {
      const index = [...label].reduce((acc, _, i) => acc + (label ? label.charCodeAt(i) : 0), 0)
      const color = DEFAULT_AVATAR_COLORS[(index + (seed || 0)) % DEFAULT_AVATAR_COLORS.length] as string
      setAvatarColor(color)
      return
    }

    setAvatarColor(null)
  }, [randomColor, blazeAvatarColor, staticColor, label, seed])

  const getBackgroundColor = () => {
    if (backgroundColor !== undefined && backgroundColor !== null) {
      return backgroundColor
    }
    if (avatarColor) {
      return avatarColor
    }
    if ((url && !error) || emoji) {
      return color
    }
    return undefined
  }

  const finalBackgroundColor = getBackgroundColor()

  return (
    <div
      className={cx(Styles.avatar, className, {
        [Styles.emoji]: !!emoji,
        [Styles.blaze]: !!blazeAvatarColor,
        [Styles.transparent]: !!url && !error && !emoji,
      })}
      style={{
        height: size,
        width: size,
        minWidth: size,
        borderWidth: blazeAvatarColor ? size * 0.05 : undefined,
        borderColor: borderColor ?? undefined,
        fontSize: emoji
          ? size * emojiMultiplier
          : Math.floor(size * fontSizeMultiplier[modifiedLabel?.length ? modifiedLabel.length - 1 : 0]),
        backgroundColor: finalBackgroundColor,
      }}
      title={title}
      onClick={onClick}
      data-name="avatar"
      data-testid="avatar"
    >
      {content}
    </div>
  )
}
export default BlazeAvatar
